import type {
  CalendarDate,
  EditorDocument,
  Instant,
  Priority,
  Task,
  TaskStatus,
} from "@hq/contracts";
import { newId } from "./ids";
import { sameDocument } from "./notes";
import { normalizeTags } from "./tags";
import type { NowContext } from "./time";

export type NewTaskInput = {
  title: string;
  description?: string;
  sourceNoteId?: string;
  triaged?: boolean;
  plannedDate?: CalendarDate;
  projectId?: string;
  priority?: Priority;
  dueDate?: CalendarDate;
  scheduledAt?: Instant;
  scheduledDurationMinutes?: number;
  evening?: boolean;
  parentTaskId?: string;
  heading?: string;
  estimateMinutes?: number;
  tags?: readonly string[];
  status?: TaskStatus;
  sortOrder?: number;
  id?: string;
};

function defined<T extends object>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out as T;
}

export function createTask(input: NewTaskInput, ctx: NowContext): Task {
  const title = input.title.trim();
  if (!title) throw new Error("Task title is required");
  return defined({
    id: input.id ?? newId(),
    createdAt: ctx.now,
    updatedAt: ctx.now,
    revision: 0,
    title,
    description: input.description,
    sourceNoteId: input.sourceNoteId,
    triaged: input.triaged,
    plannedDate: input.plannedDate,
    projectId: input.projectId,
    status: input.status ?? "open",
    priority: input.priority,
    dueDate: input.dueDate,
    scheduledAt: input.scheduledAt,
    scheduledDurationMinutes: input.scheduledDurationMinutes,
    evening: input.evening,
    parentTaskId: input.parentTaskId,
    heading: input.heading,
    estimateMinutes: input.estimateMinutes,
    tags:
      input.tags && normalizeTags(input.tags).length > 0 ? normalizeTags(input.tags) : undefined,
    sortOrder: input.sortOrder ?? Date.parse(ctx.now),
  });
}

function touch<T extends { updatedAt: Instant }>(entity: T, ctx: NowContext, patch: Partial<T>): T {
  return defined({ ...entity, ...patch, updatedAt: ctx.now });
}

/** Set-or-clear helper: `null` removes the field, undefined leaves it alone. */
function setOrClear<T extends object, K extends keyof T>(entity: T, key: K, value: T[K] | null): T {
  const next = { ...entity };
  if (value === null) delete next[key];
  else next[key] = value;
  return next;
}

export function renameTask(task: Task, title: string, ctx: NowContext): Task {
  const t = title.trim();
  if (!t) throw new Error("Task title is required");
  return touch(task, ctx, { title: t });
}

/** Plain Markdown. Drops the editor document: the Markdown is the whole description again. */
export function setTaskDescription(task: Task, description: string | null, ctx: NowContext): Task {
  const next = setOrClear(task, "description", description === "" ? null : description);
  return touch(setOrClear(next, "descriptionDocument", null), ctx, {});
}

/** The editor's document with its Markdown mirror. Empty Markdown clears both. */
export function setTaskDescriptionContent(
  task: Task,
  content: { document: EditorDocument | null; markdown: string | null },
  ctx: NowContext,
): Task {
  const markdown = content.markdown === "" ? null : content.markdown;
  const document = markdown === null ? null : content.document;
  if (
    (markdown ?? undefined) === task.description &&
    sameDocument(document ?? undefined, task.descriptionDocument)
  )
    return task;
  const next = setOrClear(
    setOrClear(task, "description", markdown),
    "descriptionDocument",
    document,
  );
  return touch(next, ctx, {});
}

export function completeTask(task: Task, ctx: NowContext): Task {
  return touch(task, ctx, { status: "completed", completedAt: ctx.now });
}

export function reopenTask(task: Task, ctx: NowContext): Task {
  return touch(setOrClear(task, "completedAt", null), ctx, { status: "open" });
}

export function setTaskStatus(task: Task, status: TaskStatus, ctx: NowContext): Task {
  if (status === "completed") return completeTask(task, ctx);
  return touch(setOrClear(task, "completedAt", null), ctx, { status });
}

export function setTaskDueDate(task: Task, dueDate: CalendarDate | null, ctx: NowContext): Task {
  return touch(setOrClear(task, "dueDate", dueDate), ctx, {});
}

/** Choosing a day replaces any timebox, never the deadline. */
export function planTask(task: Task, plannedDate: CalendarDate | null, ctx: NowContext): Task {
  const next = setOrClear(task, "plannedDate", plannedDate);
  delete next.scheduledAt;
  delete next.scheduledDurationMinutes;
  delete next.evening;
  return touch(next, ctx, {
    triaged: true,
    status: task.status === "someday" ? "open" : task.status,
  });
}

export function scheduleTask(
  task: Task,
  scheduledAt: Instant | null,
  scheduledDurationMinutes: number | null,
  ctx: NowContext,
): Task {
  let next = setOrClear(task, "scheduledAt", scheduledAt);
  delete next.plannedDate;
  next = setOrClear(
    next,
    "scheduledDurationMinutes",
    scheduledAt === null ? null : scheduledDurationMinutes,
  );
  return touch(next, ctx, {});
}

export function moveTaskToProject(task: Task, projectId: string | null, ctx: NowContext): Task {
  return touch(setOrClear(task, "projectId", projectId), ctx, {});
}

export function setTaskPriority(task: Task, priority: Priority | null, ctx: NowContext): Task {
  return touch(setOrClear(task, "priority", priority), ctx, {});
}

export function setTaskEvening(task: Task, evening: boolean, ctx: NowContext): Task {
  return touch(setOrClear(task, "evening", evening ? true : null), ctx, {});
}

export function setTaskHeading(task: Task, heading: string | null, ctx: NowContext): Task {
  return touch(setOrClear(task, "heading", heading?.trim() ? heading.trim() : null), ctx, {});
}

export function setTaskEstimate(task: Task, estimateMinutes: number | null, ctx: NowContext): Task {
  return touch(setOrClear(task, "estimateMinutes", estimateMinutes), ctx, {});
}

export function setTaskParent(task: Task, parentTaskId: string | null, ctx: NowContext): Task {
  if (parentTaskId === task.id) throw new Error("A task cannot be its own parent");
  return touch(setOrClear(task, "parentTaskId", parentTaskId), ctx, {});
}

export function setTaskTags(task: Task, tags: readonly string[], ctx: NowContext): Task {
  const normalized = normalizeTags(tags);
  return touch(setOrClear(task, "tags", normalized.length > 0 ? normalized : null), ctx, {});
}

export function reorderTask(task: Task, sortOrder: number, ctx: NowContext): Task {
  return touch(task, ctx, { sortOrder });
}

export function deleteTask(task: Task, ctx: NowContext): Task {
  return touch(task, ctx, { deletedAt: ctx.now });
}

export function restoreTask(task: Task, ctx: NowContext): Task {
  return touch(setOrClear(task, "deletedAt", null), ctx, {});
}

export function isTaskOverdue(task: Task, today: CalendarDate): boolean {
  return task.status === "open" && task.dueDate !== undefined && task.dueDate < today;
}
